import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import "./support.css";

// Help & support (2026-10-28). One small help button for signed-in people. Help answers first; a message to the
// TNS team only when the answers didn't solve it. Replies arrive live (Server-Sent Events) and by email.
// Same file in the reader and publisher apps; only `side`, the token key and the help content differ.
// Data: /api/support/* (SupportController). Messages are shown as plain text, never as HTML.
const API = `${process.env.REACT_APP_BASE_URL}/api`;

const STATUS = {
  waiting: { text: "Waiting for us", tone: "wait" },
  answered: { text: "We replied", tone: "new" },
  resolved: { text: "Solved", tone: "done" },
};

const ago = (iso) => {
  const d = new Date(iso);
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  const sameDay = new Date().toDateString() === d.toDateString();
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return time;
  if (s < 7 * 86400) return `${d.toLocaleDateString("en-GB", { weekday: "short" })} ${time}`;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

// Phone photos are big: shrink to 1600 px JPEG in the browser before sending.
const shrink = (file) =>
  new Promise((resolve) => {
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) return resolve(null);
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const k = Math.min(1, 1600 / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * k);
      c.height = Math.round(img.height * k);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      c.toBlob((b) => resolve(b ? new File([b], "screenshot.jpg", { type: "image/jpeg" }) : file), "image/jpeg", 0.85);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });

const Icon = ({ name }) => {
  const P = {
    help: <><circle cx="12" cy="12" r="9.5" /><path d="M9.3 9.2a2.8 2.8 0 1 1 3.9 2.6c-.8.4-1.2.9-1.2 1.8v.4" /><circle cx="12" cy="17" r=".6" fill="currentColor" /></>,
    close: <path d="M6 6l12 12M18 6L6 18" />,
    back: <path d="M15 5l-7 7 7 7" />,
    send: <path d="M4 12l16-8-6 16-2.5-6.5L4 12z" />,
    clip: <path d="M20 11.5l-7.8 7.8a5 5 0 0 1-7.1-7.1l8.5-8.5a3.3 3.3 0 0 1 4.7 4.7l-8.5 8.5a1.7 1.7 0 0 1-2.4-2.4l7.8-7.8" />,
    search: <><circle cx="11" cy="11" r="6.5" /><path d="M16 16l4 4" /></>,
    chev: <path d="M9 5l7 7-7 7" />,
    chat: <path d="M5 5h14a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-8l-4 3.5V16H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z" />,
    lock: <><rect x="5" y="10.5" width="14" height="9.5" rx="2" /><path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" /></>,
    calendar: <><rect x="4" y="5.5" width="16" height="14.5" rx="2" /><path d="M4 10h16M8.5 3.5v4M15.5 3.5v4" /></>,
    archive: <><rect x="3.5" y="4.5" width="17" height="4.5" rx="1" /><path d="M5 9v9.5a1.5 1.5 0 0 0 1.5 1.5h11a1.5 1.5 0 0 0 1.5-1.5V9M10 13h4" /></>,
    doc: <><path d="M7 3.5h7l4 4v12a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-15a1 1 0 0 1 1-1z" /><path d="M14 3.5v4h4M9 12.5h6M9 16h4" /></>,
    user: <><circle cx="12" cy="8.5" r="3.8" /><path d="M4.5 20c1-3.8 4-5.8 7.5-5.8s6.5 2 7.5 5.8" /></>,
    bolt: <path d="M13 3L5.5 13.5H12L11 21l7.5-10.5H12z" />,
    book: <><path d="M4 5.5c2.8-1 5.5-.8 8 .8 2.5-1.6 5.2-1.8 8-.8v13c-2.8-1-5.5-.8-8 .8-2.5-1.6-5.2-1.8-8-.8z" /><path d="M12 6.3v13" /></>,
    upload: <><path d="M12 15.5V4M7.5 8.5L12 4l4.5 4.5" /><path d="M4.5 15v3.5a1.5 1.5 0 0 0 1.5 1.5h12a1.5 1.5 0 0 0 1.5-1.5V15" /></>,
    wallet: <><rect x="3.5" y="6" width="17" height="13" rx="2" /><path d="M3.5 10h17M15.5 14.5h2" /></>,
    gear: <><circle cx="12" cy="12" r="3" /><path d="M12 3.5v2.2M12 18.3v2.2M3.5 12h2.2M18.3 12h2.2M6 6l1.6 1.6M16.4 16.4L18 18M6 18l1.6-1.6M16.4 7.6L18 6" /></>,
    people: <><circle cx="9" cy="9" r="3.3" /><path d="M3 19.5c.8-3.3 3.2-5 6-5s5.2 1.7 6 5" /><path d="M15.5 6a3 3 0 0 1 0 6M17.5 14.6c1.7.6 3 2.2 3.5 4.9" /></>,
    chart: <path d="M4 20h16M7 16.5V11M12 16.5V6.5M17 16.5v-4" />,
  };
  return <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{P[name]}</svg>;
};

export default function SupportWidget({ side, tokenKey, help, hideOn = [], signedOutPaths = [] }) {
  const history = useHistory();
  const location = useLocation();
  const [token, setToken] = useState(() => { try { return localStorage.getItem(tokenKey); } catch (e) { return null; } });
  const [open, setOpen] = useState(false);
  const [view, setView] = useState({ name: "home" }); // home | topic | form | thread | list
  const [me, setMe] = useState(null); // { firstName, threads, unread, open }
  const [allowed, setAllowed] = useState(true);
  const [query, setQuery] = useState("");
  const panelRef = useRef(null);
  const launcherRef = useRef(null);

  const call = useCallback(async (method, path, body, form) => {
    const headers = { Authorization: `Bearer ${token}` };
    let payload;
    if (form) payload = form;
    else if (body) { headers["Content-Type"] = "application/json"; payload = JSON.stringify({ side, ...body }); }
    try {
      const sep = path.includes("?") ? "&" : "?";
      const r = await fetch(`${API}${path}${method === "GET" ? `${sep}side=${side}` : ""}`, { method, headers, body: payload });
      const j = await r.json().catch(() => ({}));
      if (r.status === 401 || r.status === 403) { if (path === "/support/me") setAllowed(false); }
      return { ok: r.ok && j.success !== false, status: r.status, data: j.data, message: j.message };
    } catch (e) {
      return { ok: false, status: 0, message: "No connection. Please check your internet and try again." };
    }
  }, [token, side]);

  const refresh = useCallback(async () => {
    if (!token) return;
    const r = await call("GET", "/support/me");
    if (r.ok) { setAllowed(true); setMe(r.data); }
  }, [call, token]);

  // Sign-in state: storage events (other tabs) + a light check on navigation (same tab).
  useEffect(() => {
    const read = () => { try { setToken(localStorage.getItem(tokenKey)); } catch (e) { setToken(null); } };
    read();
    window.addEventListener("storage", read);
    return () => window.removeEventListener("storage", read);
  }, [tokenKey, location.pathname]);

  useEffect(() => {
    if (!token) { setMe(null); return undefined; }
    refresh();
    const t = setInterval(() => { if (document.visibilityState === "visible") refresh(); }, 60000);
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [token, refresh]);

  // Open from anywhere: menu items fire `tns:help-open`; email links carry ?help=open.
  useEffect(() => {
    const onOpen = (e) => { setOpen(true); if (e && e.detail && e.detail.topic) setView({ name: "topic", topic: e.detail.topic }); };
    window.addEventListener("tns:help-open", onOpen);
    return () => window.removeEventListener("tns:help-open", onOpen);
  }, []);
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (p.get("help") === "open" && token) {
      setOpen(true);
      setView({ name: "list" });
      p.delete("help");
      history.replace({ pathname: location.pathname, search: p.toString() ? `?${p}` : "" });
    }
  }, [location.search, location.pathname, token, history]);

  // Live updates while the panel is open (falls back to the 60 s refresh if the stream can't connect).
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!open || !token || typeof EventSource === "undefined") return undefined;
    let es = null;
    let stop = false;
    let retry = null;
    const connect = async () => {
      const r = await call("POST", "/support/stream_ticket", {});
      if (stop || !r.ok) return;
      es = new EventSource(`${API}/support/stream?ticket=${encodeURIComponent(r.data.ticket)}`);
      es.addEventListener("support", () => { refresh(); setTick((x) => x + 1); });
      es.onerror = () => { es.close(); if (!stop) retry = setTimeout(connect, 8000); };
    };
    connect();
    return () => { stop = true; clearTimeout(retry); if (es) es.close(); };
  }, [open, token, call, refresh]);

  // Esc closes; focus moves into the panel and back to the button.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    setTimeout(() => { const el = panelRef.current && panelRef.current.querySelector("[data-autofocus], input, textarea, button"); if (el) el.focus(); }, 60);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, view.name]);
  const close = () => { setOpen(false); setTimeout(() => launcherRef.current && launcherRef.current.focus(), 30); };

  const go = (to) => { setOpen(false); history.push(to); };
  // If an older chat bubble (tawk.to) loaded before sign-in, hide it while this help button is shown.
  useEffect(() => {
    if (!token) return;
    try { if (window.Tawk_API && typeof window.Tawk_API.hideWidget === "function") window.Tawk_API.hideWidget(); } catch (e) { /* not loaded */ }
  }, [token]);
  const hidden = !token || !allowed || hideOn.some((re) => re.test(location.pathname)) || signedOutPaths.includes(location.pathname);
  // While the help button floats, phones get room at the end of the page so it never covers the last line.
  useEffect(() => {
    document.body.classList.toggle("sp-has-launcher", !hidden);
    return () => document.body.classList.remove("sp-has-launcher");
  }, [hidden]);
  if (hidden) return null;

  const unread = (me && me.unread) || 0;
  return (
    <div className={`sp sp-${side}`}>
      {!open && (
        <button type="button" ref={launcherRef} className="sp-launcher" onClick={() => { setOpen(true); setView(unread ? { name: "list" } : { name: "home" }); }}
          aria-label={unread ? `Help and support, ${unread} new ${unread === 1 ? "reply" : "replies"}` : "Help and support"} aria-expanded={open}>
          <Icon name="help" />
          {unread ? <span className="sp-dot" aria-hidden="true">{unread > 9 ? "9+" : unread}</span> : null}
        </button>
      )}
      {open && (
        <>
          <div className="sp-scrim" onClick={close} aria-hidden="true" />
          <section className="sp-panel" role="dialog" aria-modal="true" aria-label="Help and support" ref={panelRef}>
            {view.name === "home" && (
              <Home me={me} help={help} query={query} setQuery={setQuery} onClose={close}
                onTopic={(topic) => setView({ name: "topic", topic })} onThread={(id) => setView({ name: "thread", id })}
                onList={() => setView({ name: "list" })} onAnswer={(topic, answer) => setView({ name: "topic", topic, answer })} />
            )}
            {view.name === "topic" && (
              <Topic help={help} topic={view.topic} openAnswer={view.answer} call={call} go={go} onClose={close}
                onBack={() => setView({ name: "home" })} onMessage={() => setView({ name: "form", topic: view.topic })} />
            )}
            {view.name === "form" && (
              <Form help={help} topic={view.topic} call={call} me={me} onClose={close}
                onBack={() => setView(view.topic && help.topics[view.topic] && help.topics[view.topic].answers.length ? { name: "topic", topic: view.topic } : { name: "home" })}
                onSent={(id) => { refresh(); setView({ name: "thread", id, fresh: true }); }}
                onOpenThread={(id) => setView({ name: "thread", id })} />
            )}
            {view.name === "list" && (
              <List me={me} onClose={close} onBack={() => setView({ name: "home" })} onThread={(id) => setView({ name: "thread", id })}
                onNew={() => setView({ name: "home" })} />
            )}
            {view.name === "thread" && (
              <Thread id={view.id} fresh={view.fresh} call={call} tick={tick} help={help} onClose={close}
                onBack={() => { refresh(); setView({ name: (me && me.threads.length > 1) ? "list" : "home" }); }} onChanged={refresh} />
            )}
          </section>
        </>
      )}
    </div>
  );
}

function Head({ title, sub, onBack, onClose }) {
  return (
    <header className="sp-head">
      {onBack ? <button type="button" className="sp-icon" onClick={onBack} aria-label="Back"><Icon name="back" /></button> : <span className="sp-icon-gap" />}
      <div className="sp-head-text"><h2>{title}</h2>{sub ? <p>{sub}</p> : null}</div>
      <button type="button" className="sp-icon" onClick={onClose} aria-label="Close help"><Icon name="close" /></button>
    </header>
  );
}

function Home({ me, help, query, setQuery, onClose, onTopic, onThread, onList, onAnswer }) {
  const active = me ? me.threads.filter((t) => t.status !== "resolved") : [];
  const q = query.trim().toLowerCase();
  const hits = useMemo(() => {
    if (q.length < 2) return [];
    const words = q.split(/\s+/);
    const out = [];
    Object.entries(help.topics).forEach(([key, t]) => t.answers.forEach((a) => {
      const hay = `${a.q} ${a.a} ${a.keywords || ""} ${t.label}`.toLowerCase();
      if (words.every((w) => hay.includes(w))) out.push({ topic: key, ...a });
    }));
    return out.slice(0, 6);
  }, [q, help]);
  return (
    <>
      <div className="sp-hero">
        <div className="sp-hero-top">
          <span className="sp-brand">TheNewspaperStand</span>
          <button type="button" className="sp-icon sp-icon-light" onClick={onClose} aria-label="Close help"><Icon name="close" /></button>
        </div>
        <h2>{me && me.firstName ? `Hi ${me.firstName}, how can we help?` : "How can we help?"}</h2>
        <label className="sp-search">
          <Icon name="search" />
          <input data-autofocus type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={help.searchHint} aria-label="Search help" />
        </label>
      </div>
      <div className="sp-body">
        {q.length >= 2 && (
          <div className="sp-block">
            {hits.length ? hits.map((h) => (
              <button type="button" key={`${h.topic}-${h.id}`} className="sp-row" onClick={() => onAnswer(h.topic, h.id)}>
                <span className="sp-row-main"><strong>{h.q}</strong><small>{help.topics[h.topic].label}</small></span><Icon name="chev" />
              </button>
            )) : <p className="sp-empty">No answer found for “{query}”. Pick a topic below, or <button type="button" className="sp-link" onClick={() => onTopic("other")}>message us</button>.</p>}
          </div>
        )}
        {active.length > 0 && (
          <div className="sp-block">
            <div className="sp-label">Your conversations</div>
            {active.slice(0, 2).map((t) => <ThreadRow key={t.id} t={t} onClick={() => onThread(t.id)} />)}
            {me.threads.length > 2 && <button type="button" className="sp-link sp-more" onClick={onList}>See all your messages</button>}
          </div>
        )}
        <div className="sp-block">
          <div className="sp-label">Choose a topic</div>
          <div className="sp-topics">
            {Object.entries(help.topics).map(([key, t]) => (
              <button type="button" key={key} className="sp-topic" onClick={() => onTopic(key)}>
                <span className="sp-topic-icon"><Icon name={t.icon} /></span>
                <span>{t.label}</span>
              </button>
            ))}
          </div>
        </div>
        {active.length === 0 && me && me.threads.length > 0 && (
          <button type="button" className="sp-link sp-more" onClick={onList}>Your past messages</button>
        )}
        <p className="sp-foot">{help.hours}</p>
      </div>
    </>
  );
}

function ThreadRow({ t, onClick }) {
  const st = STATUS[t.status] || STATUS.waiting;
  return (
    <button type="button" className={`sp-thread${t.unread ? " unread" : ""}`} onClick={onClick}>
      <span className="sp-row-main">
        <strong>{t.subject || t.topicLabel}</strong>
        <small>{t.preview || "…"}</small>
      </span>
      <span className="sp-thread-side">
        <span className={`sp-chip ${st.tone}`}>{t.unread ? "New reply" : st.text}</span>
        <small>{ago(t.lastMessageAt)}</small>
      </span>
    </button>
  );
}

function Topic({ help, topic, openAnswer, call, go, onBack, onClose, onMessage }) {
  const t = help.topics[topic];
  const [openId, setOpenId] = useState(openAnswer || (t && t.answers.length === 1 ? t.answers[0].id : null));
  const [votes, setVotes] = useState({});
  useEffect(() => { if (t && !t.answers.length) onMessage(); }, [t, onMessage]);
  if (!t) return null;
  const vote = (id, helpful) => {
    setVotes((v) => ({ ...v, [id]: helpful }));
    call("POST", "/support/help_vote", { topic, answer: id, helpful });
  };
  const anyNo = Object.values(votes).some((v) => v === false);
  return (
    <>
      <Head title={t.label} onBack={onBack} onClose={onClose} />
      <div className="sp-body">
        <div className="sp-answers">
          {t.answers.map((a) => {
            const isOpen = openId === a.id;
            return (
              <div key={a.id} className={`sp-answer${isOpen ? " open" : ""}`}>
                <button type="button" className="sp-answer-q" aria-expanded={isOpen} onClick={() => setOpenId(isOpen ? null : a.id)} {...(isOpen ? {} : {})}>
                  <span>{a.q}</span><Icon name="chev" />
                </button>
                {isOpen && (
                  <div className="sp-answer-a">
                    {a.a.split("\n").map((line, i) => <p key={i}>{line}</p>)}
                    {a.action && <button type="button" className="sp-btn sp-btn-soft" onClick={() => go(a.action.to)}>{a.action.label}</button>}
                    <div className="sp-vote">
                      {votes[a.id] === undefined ? (
                        <>
                          <span>Did this help?</span>
                          <button type="button" onClick={() => vote(a.id, true)}>Yes</button>
                          <button type="button" onClick={() => vote(a.id, false)}>No</button>
                        </>
                      ) : votes[a.id] ? <span className="sp-thanks">Great, glad that helped.</span> : <span className="sp-thanks">Sorry about that. Send us a message below and a person will help.</span>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className={`sp-still${anyNo ? " strong" : ""}`}>
          <div><strong>Still stuck?</strong><span>Message the TNS team. A real person will reply.</span></div>
          <button type="button" className="sp-btn" onClick={onMessage}><Icon name="chat" /> Message us</button>
        </div>
      </div>
    </>
  );
}

function Form({ help, topic: initialTopic, call, me, onBack, onClose, onSent, onOpenThread }) {
  const [topic, setTopic] = useState(initialTopic && help.topics[initialTopic] ? initialTopic : "");
  const [body, setBody] = useState(() => { try { return sessionStorage.getItem(`sp-draft-${initialTopic}`) || ""; } catch (e) { return ""; } });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);
  useEffect(() => { try { sessionStorage.setItem(`sp-draft-${topic}`, body); } catch (e) { /* private mode */ } }, [body, topic]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
  const t = help.topics[topic];
  const openSame = me ? me.threads.filter((x) => x.status !== "resolved") : [];
  const atCap = me && me.limits && openSame.length >= me.limits.openThreads;
  const pick = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    const small = await shrink(f);
    if (!small) { setError("Please choose a photo or screenshot (JPG, PNG or WebP)."); return; }
    setError("");
    setFile(small);
    setPreview(URL.createObjectURL(small));
  };
  const send = async (e) => {
    e.preventDefault();
    if (!topic) { setError("Please choose what it’s about."); return; }
    if (body.trim().length < 10) { setError("Please tell us a little more, so we can help the first time."); return; }
    setBusy(true);
    setError("");
    const fd = new FormData();
    fd.append("side", help.side);
    fd.append("topic", topic);
    fd.append("body", body.trim());
    fd.append("page", window.location.pathname);
    if (file) fd.append("image", file);
    const r = await call("POST", "/support/threads", null, fd);
    setBusy(false);
    if (!r.ok) { setError(r.message || "We couldn’t send that. Please try again."); return; }
    try { sessionStorage.removeItem(`sp-draft-${topic}`); } catch (err) { /* private mode */ }
    onSent(r.data.thread.id);
  };
  if (atCap) {
    return (
      <>
        <Head title="Message us" onBack={onBack} onClose={onClose} />
        <div className="sp-body">
          <p className="sp-note">You already have {openSame.length} conversations open with us. Please add to one of them; we’ll answer them all.</p>
          {openSame.map((x) => <ThreadRow key={x.id} t={x} onClick={() => onOpenThread(x.id)} />)}
        </div>
      </>
    );
  }
  return (
    <>
      <Head title="Message us" sub={help.replyTime} onBack={onBack} onClose={onClose} />
      <form className="sp-body sp-form" onSubmit={send} noValidate>
        <label className="sp-field">
          <span>What’s it about?</span>
          <select value={topic} onChange={(e) => setTopic(e.target.value)} data-autofocus={!topic || undefined}>
            <option value="">Choose a topic</option>
            {Object.entries(help.topics).map(([k, x]) => <option key={k} value={k}>{x.label}</option>)}
          </select>
        </label>
        <label className="sp-field">
          <span>Your message</span>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} maxLength={(me && me.limits && me.limits.body) || 3000}
            placeholder={(t && t.placeholder) || "Tell us what happened and what you expected. The more detail, the faster we can help."} data-autofocus={topic ? true : undefined} />
        </label>
        {t && t.tip ? <p className="sp-tip">{t.tip}</p> : null}
        <div className="sp-attach">
          {preview ? (
            <span className="sp-preview"><img src={preview} alt="Your screenshot" /><button type="button" className="sp-link" onClick={() => { setFile(null); setPreview(""); }}>Remove</button></span>
          ) : (
            <button type="button" className="sp-link" onClick={() => fileRef.current && fileRef.current.click()}><Icon name="clip" /> Add a screenshot (optional)</button>
          )}
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={pick} />
        </div>
        {error ? <p className="sp-error" role="alert">{error}</p> : null}
        <button type="submit" className="sp-btn sp-btn-wide" disabled={busy}>{busy ? "Sending…" : "Send message"}</button>
        <p className="sp-foot">We’ll reply here and by email. Please don’t send card numbers or passwords.</p>
      </form>
    </>
  );
}

function List({ me, onBack, onClose, onThread, onNew }) {
  const threads = (me && me.threads) || [];
  return (
    <>
      <Head title="Your messages" onBack={onBack} onClose={onClose} />
      <div className="sp-body">
        {threads.length ? threads.map((t) => <ThreadRow key={t.id} t={t} onClick={() => onThread(t.id)} />) : <p className="sp-empty">No messages yet.</p>}
        <button type="button" className="sp-btn sp-btn-soft sp-btn-wide" onClick={onNew}>Get help with something else</button>
      </div>
    </>
  );
}

function Thread({ id, fresh, call, tick, help, onBack, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [body, setBody] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sendError, setSendError] = useState("");
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const load = useCallback(async () => {
    const r = await call("GET", `/support/threads/${id}`);
    if (r.ok) { setData(r.data); setError(""); onChanged(); } else setError(r.message || "We couldn’t open that conversation.");
  }, [call, id, onChanged]);
  useEffect(() => { load(); }, [load, tick]);
  useEffect(() => { if (endRef.current) endRef.current.scrollIntoView({ block: "end" }); }, [data]);
  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() && !file) return;
    setBusy(true);
    setSendError("");
    const fd = new FormData();
    fd.append("side", help.side);
    fd.append("body", body.trim());
    if (file) fd.append("image", file);
    const r = await call("POST", `/support/threads/${id}/messages`, null, fd);
    setBusy(false);
    if (!r.ok) { setSendError(r.message || "Not sent. Please try again."); return; }
    setBody("");
    setFile(null);
    load();
  };
  const resolve = async (helpful) => { await call("POST", `/support/threads/${id}/resolve`, { helpful }); load(); };
  const pick = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    const small = await shrink(f);
    if (!small) { setSendError("Please choose a photo or screenshot."); return; }
    setFile(small);
  };
  const t = data && data.thread;
  const st = t ? STATUS[t.status] || STATUS.waiting : null;
  return (
    <>
      <Head title={t ? (t.subject || t.topicLabel) : "Conversation"} sub={t ? `#${t.number} · ${st.text}` : ""} onBack={onBack} onClose={onClose} />
      <div className="sp-body sp-chat" aria-live="polite">
        {error ? <p className="sp-error">{error}</p> : null}
        {!data && !error ? <p className="sp-empty">Loading…</p> : null}
        {fresh && t && t.status === "waiting" ? <p className="sp-note">Thanks, we’ve got your message. {help.replyTime} You can close this; we’ll email you when we reply.</p> : null}
        {data && data.messages.map((m) => (
          m.from === "system" ? <p key={m.id} className="sp-system">{m.body}</p> : (
            <div key={m.id} className={`sp-msg ${m.from === "customer" ? "mine" : "team"}`}>
              {m.from === "team" ? <span className="sp-who">{m.authorName} · TNS</span> : null}
              {m.body ? <div className="sp-bubble">{m.body}</div> : null}
              {m.image ? <a className="sp-img" href={m.image} target="_blank" rel="noopener noreferrer"><img src={m.image} alt="Attached screenshot" /></a> : null}
              <time dateTime={m.createdAt}>{ago(m.createdAt)}</time>
            </div>
          )
        ))}
        {t && t.status === "answered" ? (
          <div className="sp-solved">
            <span>Did this solve it?</span>
            <button type="button" className="sp-btn sp-btn-soft" onClick={() => resolve(true)}>Yes, thanks</button>
          </div>
        ) : null}
        {t && t.status === "resolved" ? <p className="sp-system">Solved. Write below if you need anything else; it will open again.</p> : null}
        <div ref={endRef} />
      </div>
      {t && (
        <form className="sp-compose" onSubmit={send}>
          {file ? <span className="sp-chipfile">Screenshot attached <button type="button" onClick={() => setFile(null)} aria-label="Remove screenshot">×</button></span> : null}
          {sendError ? <p className="sp-error" role="alert">{sendError}</p> : null}
          <div className="sp-compose-row">
            <button type="button" className="sp-icon" onClick={() => fileRef.current && fileRef.current.click()} aria-label="Attach a screenshot"><Icon name="clip" /></button>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={1} placeholder="Write a message…" aria-label="Write a message" maxLength={3000}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && window.innerWidth > 700) { e.preventDefault(); send(e); } }} />
            <button type="submit" className="sp-send" disabled={busy || (!body.trim() && !file)} aria-label="Send"><Icon name="send" /></button>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={pick} />
          </div>
        </form>
      )}
    </>
  );
}
